const BRAZIL_TIME_ZONE = 'America/Sao_Paulo';

const getTodayIsoInBrazil = (referenceDate: Date = new Date()): string => (
  referenceDate.toLocaleDateString('en-CA', { timeZone: BRAZIL_TIME_ZONE })
);

export const BODY_PHOTO_ASSESSMENT_MONTH_LABELS = [
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro',
] as const;

export type BodyPhotoAssessmentDateParts = {
  day: string;
  month: string;
  year: string;
};

const MIN_ASSESSMENT_YEAR = 2000;

export const formatBodyPhotoAssessmentMonthLabel = (monthIndex: number): string => {
  const label = BODY_PHOTO_ASSESSMENT_MONTH_LABELS[monthIndex - 1];
  if (!label) {
    return '';
  }

  return label.charAt(0).toUpperCase() + label.slice(1);
};

export const getBodyPhotoAssessmentYearOptions = (
  referenceDate: Date = new Date(),
): number[] => {
  const currentYear = Number(
    referenceDate.toLocaleDateString('en-CA', { timeZone: BRAZIL_TIME_ZONE }).slice(0, 4),
  );
  const lastYear = Number.isFinite(currentYear) ? currentYear : new Date().getFullYear();
  const years: number[] = [];

  for (let year = lastYear; year >= MIN_ASSESSMENT_YEAR; year -= 1) {
    years.push(year);
  }

  return years;
};

export const getDaysInBodyPhotoAssessmentMonth = (month: number, year: number): number => {
  if (!Number.isInteger(month) || month < 1 || month > 12 || !Number.isInteger(year)) {
    return 31;
  }

  return new Date(year, month, 0).getDate();
};

export const getBodyPhotoAssessmentDayOptions = (
  month: number,
  year: number,
): number[] => {
  const daysInMonth = getDaysInBodyPhotoAssessmentMonth(month, year);
  return Array.from({ length: daysInMonth }, (_, index) => index + 1);
};

export const getTodayBodyPhotoAssessmentDateParts = (
  referenceDate: Date = new Date(),
): BodyPhotoAssessmentDateParts => {
  const iso = getTodayIsoInBrazil(referenceDate);
  const [year, month, day] = iso.split('-');

  return {
    day: day ?? '1',
    month: month ?? '1',
    year: year ?? String(referenceDate.getFullYear()),
  };
};

export const clampBodyPhotoAssessmentDateParts = (
  parts: BodyPhotoAssessmentDateParts,
): BodyPhotoAssessmentDateParts => {
  const month = Number.parseInt(parts.month, 10);
  const year = Number.parseInt(parts.year, 10);
  const day = Number.parseInt(parts.day, 10);

  if (!Number.isInteger(month) || !Number.isInteger(year) || !Number.isInteger(day)) {
    return parts;
  }

  const maxDay = getDaysInBodyPhotoAssessmentMonth(month, year);
  const clampedDay = Math.min(Math.max(day, 1), maxDay);

  return {
    ...parts,
    day: String(clampedDay),
  };
};

export const bodyPhotoAssessmentDatePartsToIso = (
  parts: BodyPhotoAssessmentDateParts,
): string | null => {
  const month = Number.parseInt(parts.month, 10);
  const year = Number.parseInt(parts.year, 10);
  const day = Number.parseInt(parts.day, 10);

  if (
    !Number.isInteger(month)
    || month < 1
    || month > 12
    || !Number.isInteger(year)
    || year < MIN_ASSESSMENT_YEAR
    || !Number.isInteger(day)
    || day < 1
  ) {
    return null;
  }

  const maxDay = getDaysInBodyPhotoAssessmentMonth(month, year);
  if (day > maxDay) {
    return null;
  }

  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
};

export const isValidBodyPhotoAssessmentDateParts = (
  parts: BodyPhotoAssessmentDateParts,
): boolean => bodyPhotoAssessmentDatePartsToIso(parts) !== null;
